(() => {
  const materialLabels = {
    all: 'All materials',
    plain: 'Plain synthetic leather',
    scrub: 'Scrub-textured synthetic leather',
    denim: 'Cotton denim fabric',
    hibiscus: 'Hibiscus embossed synthetic leather',
  };

  class DwanCollectionFilter extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.gridContainer = this.querySelector('#ProductGridContainer');
      this.materialSelect = this.querySelector('[data-dwan-material-select]');
      this.brandSelect = this.querySelector('[data-dwan-brand-select]');
      this.toolbar = this.querySelector('[data-dwan-filter-controls]');
      if (!this.gridContainer || !this.materialSelect || !this.brandSelect || !this.toolbar) return;
      this.events = new AbortController();
      const signal = this.events.signal;
      this.addEventListener('change', (event) => this.onChange(event), { signal });
      this.addEventListener('click', (event) => this.onClick(event), { signal, capture: true });
      this.addEventListener('keyup', (event) => {
        if (event.code === 'Space') this.prepareFacetLink(event.target.closest('facet-remove a'));
      }, { signal, capture: true });
      window.addEventListener('popstate', () => this.restoreAfterHistory(), { signal });
      window.addEventListener('pageshow', () => this.restoreAfterHistory(), { signal });
      this.observer = new MutationObserver(() => this.apply());
      this.observer.observe(this.gridContainer, { childList: true });
      this.imagePriorityObserver = new MutationObserver(() => this.syncImagePriority());
      this.imagePriorityObserver.observe(this.gridContainer, { childList: true, subtree: true });
      this.toolbar.hidden = false;
      this.apply();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.observer?.disconnect();
      this.imagePriorityObserver?.disconnect();
      if (this.historyFrame != null) window.cancelAnimationFrame(this.historyFrame);
      this.historyFrame = null;
      this.events = null;
    }

    syncImagePriority() {
      const grid = this.gridContainer?.querySelector('#product-grid');
      if (!grid) return;
      this.imageLoadingStates ??= new WeakMap();
      let priorityAssigned = false;
      grid.querySelectorAll(':scope > [data-dwan-priority-card]').forEach((card) => {
        const image = card.querySelector('.card__media > .media > img');
        if (!image) return;
        if (!this.imageLoadingStates.has(image)) {
          this.imageLoadingStates.set(image, image.getAttribute('loading'));
        }
        const isPriority = !priorityAssigned && !grid.hidden && !card.hidden;
        const priority = isPriority ? 'high' : 'auto';
        if (image.getAttribute('fetchpriority') !== priority) image.setAttribute('fetchpriority', priority);
        const loading = isPriority ? 'eager' : this.imageLoadingStates.get(image);
        if (loading === null) {
          if (image.hasAttribute('loading')) image.removeAttribute('loading');
        } else if (image.getAttribute('loading') !== loading) {
          image.setAttribute('loading', loading);
        }
        if (isPriority) priorityAssigned = true;
      });
    }

    syncBrand() {
      const currentBrand = Array.from(this.brandSelect.options).find((option) => option.defaultSelected);
      if (currentBrand) this.brandSelect.value = currentBrand.value;
    }

    restoreAfterHistory() {
      // Native history form restoration may finish after pageshow/popstate.
      // One coalesced rendering callback runs after that navigation task.
      if (this.historyFrame != null) window.cancelAnimationFrame(this.historyFrame);
      this.historyFrame = window.requestAnimationFrame(() => {
        this.historyFrame = null;
        if (this.events && !this.events.signal.aborted) this.apply();
      });
    }

    material() {
      const value = new URL(window.location.href).searchParams.get('dwan_material') || 'all';
      if (!Object.hasOwn(materialLabels, value)) return 'all';
      return Array.from(this.materialSelect?.options || []).some((option) => option.value === value) ? value : 'all';
    }

    updateUrl(material) {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      if (material === 'all') url.searchParams.delete('dwan_material');
      else url.searchParams.set('dwan_material', material);
      if (url.href !== window.location.href) {
        history.pushState({ ...history.state, searchParams: url.search.slice(1) }, '', url);
      }
      this.apply();
    }

    onChange(event) {
      if (event.target === this.materialSelect) this.updateUrl(this.materialSelect.value);
      if (event.target === this.brandSelect) {
        const url = new URL(this.brandSelect.value, window.location.origin);
        url.search = window.location.search;
        url.searchParams.delete('page');
        url.searchParams.delete('section_id');
        if (this.closest('dwan-macbook-family')) url.searchParams.delete('dwan_model');
        // Keep the outgoing page's persisted form value tied to its own brand.
        this.syncBrand();
        window.location.assign(url.href);
      }
    }

    onClick(event) {
      const clear = event.target.closest('[data-dwan-clear-material]');
      if (clear && this.contains(clear)) {
        event.preventDefault();
        this.updateUrl('all');
        this.materialSelect.focus();
        return;
      }
      const all = event.target.closest('[data-dwan-clear-all]');
      if (all && this.contains(all)) {
        const url = new URL(window.location.href);
        Array.from(url.searchParams.keys()).forEach((key) => {
          if (key.startsWith('filter.') || key === 'dwan_material' || key === 'page') url.searchParams.delete(key);
        });
        all.href = url.href;
        return;
      }
      this.prepareFacetLink(event.target.closest('facet-remove a'));
    }

    prepareFacetLink(link) {
      if (!link || !this.contains(link)) return;
      const url = new URL(link.href);
      const current = new URL(window.location.href);
      const clearAll = link.matches('.active-facets__button-remove')
        || (link.matches('.mobile-facets__clear') && Boolean(link.closest('.mobile-facets__footer')));
      url.searchParams.delete('page');
      if (clearAll || this.material() === 'all') url.searchParams.delete('dwan_material');
      else url.searchParams.set('dwan_material', this.material());
      current.searchParams.forEach((value, key) => {
        if (!key.startsWith('filter.') && !['page', 'section_id', 'dwan_material'].includes(key) && !url.searchParams.has(key)) {
          url.searchParams.append(key, value);
        }
      });
      link.href = url.href;
    }

    syncNativeForms(material) {
      const forms = Array.from(this.querySelectorAll('facet-filters-form form'));
      const desktop = forms.find((form) => form.id === 'FacetFiltersForm')
        || forms.find((form) => form.id === 'FacetSortForm' || form.id === 'FacetSortDrawerForm');
      const mobile = forms.find((form) => form.id === 'FacetFiltersFormMobile');
      forms.forEach((form) => {
        const fields = Array.from(form.querySelectorAll('input[name="dwan_material"]'));
        fields.slice(1).forEach((field) => field.remove());
        const field = fields[0] || document.createElement('input');
        field.type = 'hidden';
        field.name = 'dwan_material';
        field.value = material;
        field.disabled = material === 'all' || (form !== desktop && form !== mobile);
        if (!field.parentElement) form.append(field);
      });
    }

    apply() {
      this.syncBrand();
      const empty = this.gridContainer.querySelector('[data-dwan-material-empty]');
      if (!empty) {
        this.toolbar.hidden = true;
        this.removeAttribute('data-dwan-filter-ready');
        this.syncNativeForms('all');
        return;
      }
      this.toolbar.hidden = false;
      this.setAttribute('data-dwan-filter-ready', '');
      const material = this.material();
      const url = new URL(window.location.href);
      const requestedMaterial = url.searchParams.get('dwan_material');
      if (requestedMaterial && requestedMaterial !== material) {
        url.searchParams.delete('dwan_material');
        history.replaceState({ ...history.state, searchParams: url.search.slice(1) }, '', url);
      }
      this.materialSelect.value = material;
      this.syncNativeForms(material);
      this.querySelectorAll('[data-dwan-clear-material]').forEach((button) => { button.disabled = material === 'all'; });
      const grid = this.gridContainer.querySelector('#product-grid');
      const cards = grid ? Array.from(grid.querySelectorAll(':scope > [data-dwan-material-card]')) : [];
      let families = 0;
      let colors = 0;
      let others = 0;
      cards.forEach((card) => {
        card.hidden = material !== 'all' && card.dataset.dwanMaterialCard !== material;
        if (card.hidden) return;
        if (card.dataset.dwanMaterialCard) {
          families += 1;
          colors += Number(card.dataset.dwanColorCount) || 0;
        } else others += 1;
      });
      if (grid) {
        grid.querySelectorAll(':scope > [data-dwan-dell-region-heading]').forEach((heading) => {
          heading.hidden = !cards.some((card) => !card.hidden && card.dataset.dwanDellRegion === heading.dataset.dwanDellRegionHeading);
        });
      }
      const visible = families + others;
      const count = `${families} material series / ${colors} colour products${others ? ` / ${others} other products` : ''}`;
      this.querySelectorAll('#ProductCount, #ProductCountDesktop, .mobile-facets__count').forEach((element) => { element.textContent = count; });
      const status = this.querySelector('[data-dwan-filter-status]');
      if (status && status.textContent !== count) status.textContent = count;
      empty.hidden = material === 'all' || visible > 0;
      empty.querySelector('[data-dwan-material-empty-message]').textContent = `${materialLabels[material]} is not in the current results for this laptop brand. Choose another material or clear the filters.`;
      if (grid) {
        if (grid.hasAttribute('data-dwan-native-empty')) grid.hidden = !empty.hidden;
        else grid.hidden = visible === 0;
      }
      this.syncImagePriority();
      this.querySelectorAll('facet-remove a').forEach((link) => this.prepareFacetLink(link));
    }
  }

  if (!customElements.get('dwan-collection-filter')) customElements.define('dwan-collection-filter', DwanCollectionFilter);
})();

